import { useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getBanners, createBanner, updateBanner, deleteBanner, getMenus, getCategories, uploadImage } from "@/api/endpoints";
import type { Banner } from "@/api/endpoints";
import { useI18n } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { ImageCropDialog } from "@/components/image-crop-dialog";
import { Plus, Trash2, Edit2, Upload, Image } from "lucide-react";

export function BannersPage() {
  const { venueId } = useParams<{ venueId: string }>();
  const qc = useQueryClient();
  const { t } = useI18n();
  const [formOpen, setFormOpen] = useState(false);
  const [editBanner, setEditBanner] = useState<Banner | null>(null);

  const { data: banners, isLoading } = useQuery({
    queryKey: ["banners", venueId],
    queryFn: () => getBanners(venueId!),
    enabled: !!venueId,
  });

  const delMutation = useMutation({
    mutationFn: deleteBanner,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["banners", venueId] }),
  });

  return (
    <div className="max-w-2xl">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-bold">Banner</h1>
        <Button onClick={() => { setEditBanner(null); setFormOpen(true); }}>
          <Plus className="mr-2 size-4" /> Banner Ekle
        </Button>
      </div>

      {isLoading ? (
        <p className="py-8 text-center text-muted-foreground">{t("loading")}</p>
      ) : !banners?.length ? (
        <div className="flex flex-col items-center justify-center py-16">
          <Image className="mb-4 size-12 text-muted-foreground" />
          <p className="text-muted-foreground">Henüz banner eklenmemiş</p>
        </div>
      ) : (
        <div className="space-y-3">
          {banners.map((b) => (
            <Card key={b._id}>
              <CardContent className="pt-4">
                <div className="flex gap-4">
                  <img src={b.image} alt="" className="h-20 w-40 rounded-lg object-cover" />
                  <div className="flex-1">
                    {b.title && <p className="font-semibold">{b.title}</p>}
                    <div className="mt-1 flex flex-wrap gap-2 text-xs text-muted-foreground">
                      {b.startsAt && <span>Başlangıç: {new Date(b.startsAt).toLocaleDateString("tr-TR")}</span>}
                      {b.endsAt && <span>Bitiş: {new Date(b.endsAt).toLocaleDateString("tr-TR")}</span>}
                      {!b.isActive && <span className="text-destructive">Pasif</span>}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" className="size-8" onClick={() => { setEditBanner(b); setFormOpen(true); }}>
                      <Edit2 className="size-4" />
                    </Button>
                    <ConfirmDialog
                      trigger={<Button variant="ghost" size="icon" className="size-8"><Trash2 className="size-4 text-destructive" /></Button>}
                      title="Banner Sil"
                      description="Bu banner kalıcı olarak silinecek."
                      confirmLabel={t("delete")}
                      onConfirm={() => delMutation.mutate(b._id)}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <BannerFormDialog
        open={formOpen}
        onClose={() => { setFormOpen(false); setEditBanner(null); }}
        banner={editBanner}
        venueId={venueId!}
      />
    </div>
  );
}

function BannerFormDialog({ open, onClose, banner, venueId }: { open: boolean; onClose: () => void; banner: Banner | null; venueId: string }) {
  const { t } = useI18n();
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);

  const [image, setImage] = useState("");
  const [title, setTitle] = useState("");
  const [menuId, setMenuId] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [startsAt, setStartsAt] = useState("");
  const [endsAt, setEndsAt] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [cropSrc, setCropSrc] = useState("");
  const [showCrop, setShowCrop] = useState(false);
  const [uploading, setUploading] = useState(false);

  const { data: menus } = useQuery({
    queryKey: ["menus", venueId],
    queryFn: () => getMenus(venueId),
    enabled: !!venueId,
  });

  const { data: categories } = useQuery({
    queryKey: ["categories", menuId],
    queryFn: () => getCategories(menuId),
    enabled: !!menuId,
  });

  useState(() => {
    if (banner) {
      setImage(banner.image);
      setTitle(banner.title || "");
      setMenuId(banner.menuId || "");
      setCategoryId(banner.categoryId || "");
      setStartsAt(banner.startsAt?.split("T")[0] || "");
      setEndsAt(banner.endsAt?.split("T")[0] || "");
      setIsActive(banner.isActive);
    } else {
      setImage(""); setTitle(""); setMenuId(""); setCategoryId("");
      setStartsAt(""); setEndsAt(""); setIsActive(true);
    }
  });

  const saveMutation = useMutation({
    mutationFn: () => {
      const data = {
        image,
        title: title || undefined,
        menuId: menuId || undefined,
        categoryId: categoryId || undefined,
        startsAt: startsAt || undefined,
        endsAt: endsAt || undefined,
        isActive,
      };
      return banner ? updateBanner(banner._id, data) : createBanner(venueId, data);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["banners", venueId] });
      onClose();
    },
  });

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => { setCropSrc(reader.result as string); setShowCrop(true); };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{banner ? "Banner Düzenle" : "Yeni Banner"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={(e) => { e.preventDefault(); if (image) saveMutation.mutate(); }} className="space-y-4">
          {/* Image */}
          <div>
            <Label>{t("image")} <span className="text-destructive">*</span></Label>
            <div className="mt-2 space-y-3">
              {image && <img src={image} alt="" className="h-24 w-48 rounded-lg object-cover" />}
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFileSelect} />
              <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={uploading}>
                {uploading ? t("uploading") : <><Upload className="mr-2 size-4" />{t("upload")}</>}
              </Button>
            </div>
            <ImageCropDialog open={showCrop} onClose={() => { setShowCrop(false); setCropSrc(""); }} imageSrc={cropSrc} aspectRatio={2} onCropped={async (file) => {
              setUploading(true);
              try { setImage(await uploadImage(file, "banner" as "item", venueId)); } finally { setUploading(false); }
            }} />
          </div>

          {/* Title */}
          <div>
            <Label>Başlık (opsiyonel)</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} />
          </div>

          {/* Menu link */}
          <div>
            <Label>Hedef Menü (opsiyonel)</Label>
            <Select value={menuId || "none"} onValueChange={(v) => { setMenuId(v === "none" ? "" : (v ?? "")); setCategoryId(""); }}>
              <SelectTrigger><SelectValue placeholder="Seçiniz" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Yok</SelectItem>
                {menus?.map((m) => (
                  <SelectItem key={m.id || (m as unknown as Record<string, string>)._id} value={m.id || (m as unknown as Record<string, string>)._id}>{m.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Category link */}
          {menuId && categories && categories.length > 0 && (
            <div>
              <Label>Hedef Kategori (opsiyonel)</Label>
              <Select value={categoryId || "none"} onValueChange={(v) => setCategoryId(v === "none" ? "" : (v ?? ""))}>
                <SelectTrigger><SelectValue placeholder="Seçiniz" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Tümü</SelectItem>
                  {categories.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Date range */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Başlangıç</Label>
              <Input type="date" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} />
            </div>
            <div>
              <Label>Bitiş</Label>
              <Input type="date" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} />
            </div>
          </div>

          {/* Active */}
          <div className="flex items-center gap-2">
            <Switch checked={isActive} onCheckedChange={setIsActive} />
            <Label>{t("active")}</Label>
          </div>

          <Button type="submit" className="w-full" disabled={!image || saveMutation.isPending}>
            {saveMutation.isPending ? t("saving") : t("save")}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
